import { CloseIcon } from "@chakra-ui/icons";
import { Box, Button, Code, IconButton, Input, InputGroup, InputRightElement, Text } from "@chakra-ui/react";
import {
  components,
  GetOptionLabel,
  GroupBase,
  MultiValue,
  OptionProps,
  OptionsOrGroups,
  Props,
  Select,
  SingleValueProps,
  Size,
} from "chakra-react-select";
import isEqual from "lodash.isequal";
import { ReactElement, useCallback, useState } from "react";
import { Noop } from "react-hook-form";

import { useIsTouchDevice } from "../../hooks";
import { DeleteIcon, EditIcon, PlusIcon, SaveIcon } from "../Icons/Icons";

interface BaseSearchSelectProperties<T, IsMulti extends boolean = false>
  extends Partial<Pick<Props, "isSearchable" | "onInputChange">> {
  alwaysCreatable?: boolean;
  creatableFunction?: (inputValue: string) => Promise<T> | T | undefined;
  creatableLabel?: string;
  getOptionLabel?: GetOptionLabel<T>;
  highlightResults?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  isEditable?: boolean;
  name: string;
  onBlur?: Noop;
  onChange: (value: IsMulti extends false ? null | T : MultiValue<T>) => void;
  onDelete?: (data: { label: string; value: { id: string } }) => void;
  onMenuScrollToBottom?: () => void;
  onSaveEdit?: (data: { label: string; value: { id: string } }) => void;
  options: OptionsOrGroups<T, GroupBase<T>>;
  placeholder?: string;
  renderOption?: (properties: OptionProps<T, IsMulti, GroupBase<T>>) => ReactElement;
  setValue?: (value: T) => void;
  singleValue?: (properties: SingleValueProps<T, IsMulti, GroupBase<T>>) => ReactElement;
  size?: Size;
  // TODO: Review the implementation of the `value` property. The current approach may not handle all edge cases or align with expected behavior. Consider refactoring or providing additional context for its usage.
  value: null | T | T[];
  defaultValue?: null | T | T[];
}

export type SearchSelectProperties<T, IsMulti extends boolean = false> = BaseSearchSelectProperties<T, IsMulti>;

const SearchEdit = ({
  data,
  onCancel,
  onSaveEdit,
}: {
  data: { label: string; value: { id: string } };
  onCancel: () => void;
  onSaveEdit: (value: { label: string; value: { id: string } }) => void;
}) => {
  const [editedValue, setEditedValue] = useState<string>(data.label);

  return (
    <>
      <Text fontSize="xs" mt={2}>
        Edition de:{" "}
        <Code colorScheme="gray" fontSize="xs">
          {data.label}
        </Code>
      </Text>
      <InputGroup mt={2} w="full">
        <Input
          onChange={(event) => setEditedValue(event.target.value)}
          placeholder={`Remplacer "${data?.label}"`}
          value={editedValue}
        />
        <InputRightElement justifyContent="end" pr={2}>
          <IconButton
            _hover={{ backgroundColor: "blackAlpha.100" }}
            aria-label="edit"
            colorScheme="blackAlpha"
            onClick={() => {
              onSaveEdit?.({ ...data, label: editedValue });
              onCancel();
            }}
            size="sm"
            variant="ghost"
          >
            <SaveIcon />
          </IconButton>
          <IconButton
            _hover={{ backgroundColor: "blackAlpha.100" }}
            aria-label="cancel"
            colorScheme="blackAlpha"
            onClick={onCancel}
            size="sm"
            variant="ghost"
          >
            <CloseIcon />
          </IconButton>
        </InputRightElement>
      </InputGroup>
    </>
  );
};

export const SearchSelect = <T,>({
  alwaysCreatable = false,
  creatableFunction,
  creatableLabel,
  getOptionLabel,
  highlightResults = false,
  isClearable = true,
  isDisabled,
  isEditable,
  onDelete,
  onSaveEdit,
  options,
  placeholder,
  renderOption: RenderOption,
  setValue,
  singleValue: SingleValue,
  ...rest
}: SearchSelectProperties<T>): ReactElement => {
  const isTouch = useIsTouchDevice();
  const [valueToEdit, setValueToEdit] = useState<null | T>(null);

  const createAndSelect = useCallback(
    async (input: string) => {
      const created = await creatableFunction?.(input);
      if (created) {
        setValue?.(created);
      }
    },
    [creatableFunction, setValue],
  );

  return (
    <Box>
      <Select<T>
        chakraStyles={{
          dropdownIndicator: (provided) => ({
            ...provided,
            bg: "transparent",
            cursor: "pointer",
            px: 2,
          }),
          group: (provided) => ({
            ...provided,
            pb: 4,
          }),
          groupHeading: (provided) => ({
            ...provided,
            color: "gray.500",
            fontSize: "md",
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            display: "none",
          }),
          menu: (provided) => ({
            ...provided,
            shadow: "lg",
            zIndex: "10",
          }),
          menuList: (provided) => ({
            ...provided,
            minWidth: "none",
            ...(highlightResults ? { border: "2px", borderColor: "blue.500" } : {}),
          }),
          noOptionsMessage: (provided) => ({
            ...provided,
            p: 0,
            px: 2,
          }),
          placeholder: (provided) => ({
            ...provided,
            color: "gray.500",
          }),
        }}
        components={{
          ...(isEditable
            ? {
                Option: (properties) => {
                  return (
                    <components.Option {...properties}>
                      <Box display="flex">
                        <Box flex="1">{properties.children}</Box>
                        <IconButton
                          _hover={{ backgroundColor: "blackAlpha.100" }}
                          aria-label="edit"
                          className={properties.className}
                          color="blackAlpha"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            setValueToEdit(properties.data);
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          _hover={{ backgroundColor: "blackAlpha.100" }}
                          aria-label="edit"
                          className={properties.className}
                          color="blackAlpha"
                          onClick={(event) => {
                            onDelete?.(
                              properties.data as {
                                label: string;
                                value: { id: string };
                              },
                            );
                            event.stopPropagation();
                            event.preventDefault();
                          }}
                          size="sm"
                          variant="ghost"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </components.Option>
                  );
                },
              }
            : {}),

          ...(SingleValue
            ? {
                SingleValue: (properties) => (
                  <components.SingleValue {...properties}>
                    <SingleValue {...properties} />
                  </components.SingleValue>
                ),
              }
            : {}),
          ...(RenderOption
            ? {
                Option: (properties) => {
                  const isLast = JSON.stringify(properties.options.at(-1)) === JSON.stringify(properties.data);
                  return (
                    <>
                      <Box
                        _hover={{ backgroundColor: "gray.100" }}
                        background={properties.isSelected ? "blue.500" : undefined}
                        color={properties.isSelected ? "white" : undefined}
                        cursor="pointer"
                        onClick={() => properties.selectOption(properties.data)}
                        px="3"
                        py={isTouch ? "4" : "2"}
                        w="full"
                      >
                        {RenderOption ? <RenderOption {...properties} /> : properties.children}
                      </Box>
                      {isLast && creatableFunction && alwaysCreatable && (
                        <Box pt={isTouch ? "4" : "2"} px="3">
                          <Button
                            colorScheme="info"
                            leftIcon={<PlusIcon />}
                            onClick={() => createAndSelect("")}
                            width="full"
                          >
                            Ajouter
                          </Button>
                        </Box>
                      )}
                    </>
                  );
                },
              }
            : {}),
        }}
        getOptionLabel={getOptionLabel}
        isClearable={isClearable}
        isOptionSelected={(option, currentValue) =>
          currentValue[0] &&
          (getOptionLabel
            ? getOptionLabel?.(option) === getOptionLabel?.(currentValue[0])
            : isEqual(option, currentValue[0]))
        }
        menuPlacement="auto"
        menuPortalTarget={document.body}
        noOptionsMessage={({ inputValue }) => {
          if (creatableFunction && inputValue.trim()) {
            return (
              <Button
                colorScheme="info"
                leftIcon={<PlusIcon />}
                onClick={() => createAndSelect(inputValue)}
                width="full"
              >
                {creatableLabel ?? `Ajouter ${inputValue}`}
              </Button>
            );
          }
          return <Text>Aucun résultat</Text>;
        }}
        options={options}
        placeholder={placeholder}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) as any }}
        {...rest}
        isDisabled={!!isDisabled || !!valueToEdit}
      />

      {/* TODO: fix the type of all select */}
      {isEditable && valueToEdit && onSaveEdit && (
        <SearchEdit
          data={{
            label: (valueToEdit as any)?.label,
            value: { id: (valueToEdit as any)?.value?.id },
          }}
          onCancel={() => setValueToEdit(null)}
          onSaveEdit={onSaveEdit}
        />
      )}
    </Box>
  );
};
